import { Box, Checkbox, FormControlLabel, Grid, Typography } from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { AddressFields, PhoneNumberField, ReadOnlyField } from '../../components/form';
import { Order, OrderShippingFormData } from '../../types';
import { formatPhoneNumber, logDebug } from '../../utils';
import OrderUpdateDialog, { OrderFormData } from './order-update-dialog';

interface OrderShippingDialogProps {
  open: boolean;
  onClose: () => void;
  order: Order;
  onSubmit: (data: OrderShippingFormData) => Promise<void>;
  isSubmitting?: boolean;
}

const OrderShippingForm: React.FC = () => {
  const { control, watch, setValue, getValues, trigger, reset, resetField, unregister } =
    useFormContext<OrderShippingFormData>();
  const [showConfirm, setShowConfirm] = useState(false);
  const [editedShippingState, setEditedShippingState] = useState<string | undefined>();

  const isPickup = watch('pickup');
  const useBillingAddress = watch('useBillingAddress');
  const shippingState = watch('shippingAddress.state');
  const buildingState = watch('buildingState');
  const billingAddress = watch('billingAddress');

  // Helper function to clear shipping address fields
  const clearShippingAddressFields = useCallback(() => {
    unregister([
      'shippingAddress.address1',
      'shippingAddress.address2',
      'shippingAddress.city',
      'shippingAddress.state',
      'shippingAddress.zip',
      'shippingAddress.phone',
      'shippingAddress.email',
    ]);
    resetField('shippingAddress', { defaultValue: undefined });
  }, [unregister, resetField]);

  // Helper function to handle state confirmation logic
  const handleStateConfirmation = useCallback(
    (state: string, shouldShowConfirm: boolean) => {
      if (!state || state === '') {
        return;
      }
      if (state === buildingState) {
        setShowConfirm(false);
        setValue('stateConfirmed', true, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false,
        });
      } else {
        setEditedShippingState(state);
        setValue('stateConfirmed', false, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false,
        });
        setShowConfirm(shouldShowConfirm);
      }
    },
    [buildingState, setValue, setShowConfirm, setEditedShippingState]
  );

  // Helper function to handle billing address logic
  const handleBillingAddressLogic = useCallback(
    (stateConfirmed: boolean, billingState: string, hasShippingAddress: boolean) => {
      // Always clear shipping address fields first
      clearShippingAddressFields();

      if (!stateConfirmed) {
        handleStateConfirmation(billingState, true);
        // Explicitly mark form as dirty
        setValue('useBillingAddress', true, { shouldDirty: true });
        return;
      }

      if (hasShippingAddress) {
        setEditedShippingState(billingState);
        setValue('stateConfirmed', false, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false,
        });
        setShowConfirm(true);
      }

      // Explicitly mark useBillingAddress as dirty
      setValue('useBillingAddress', true, { shouldDirty: true });
    },
    [
      handleStateConfirmation,
      clearShippingAddressFields,
      setEditedShippingState,
      setValue,
      setShowConfirm,
    ]
  );

  useEffect(() => {
    const { shippingAddress, stateConfirmed } = getValues();
    if (isPickup) {
      clearShippingAddressFields();
      setValue('useBillingAddress', false);
      setValue('stateConfirmed', false, {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
      trigger().catch(reason => {
        logDebug(reason);
      });
      setEditedShippingState(undefined);
      return;
    }

    if (useBillingAddress) {
      // Use stored billing address state when "Use billing address" is selected
      const billingState = billingAddress?.state || '';
      handleBillingAddressLogic(!!stateConfirmed, billingState, !!shippingAddress?.address1);
      return;
    }

    if (shippingAddress?.address1) {
      if (!stateConfirmed) {
        handleStateConfirmation(shippingAddress.state, true);
      } else if (shippingAddress.state && shippingAddress.state !== editedShippingState) {
        setEditedShippingState(shippingAddress.state);
        setValue('stateConfirmed', false, {
          shouldValidate: false,
          shouldDirty: true,
          shouldTouch: false,
        });
      }
    } else {
      setEditedShippingState(undefined);
      setShowConfirm(false);
      setValue('stateConfirmed', false, {
        shouldValidate: false,
        shouldDirty: true,
        shouldTouch: false,
      });
    }
  }, [
    setShowConfirm,
    setValue,
    isPickup,
    useBillingAddress,
    shippingState,
    editedShippingState,
    setEditedShippingState,
    getValues,
    trigger,
    reset,
    resetField,
    unregister,
    clearShippingAddressFields,
    handleStateConfirmation,
    handleBillingAddressLogic,
    billingAddress,
  ]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 3 }}>
          <Controller
            name="pickup"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={field.value || false}
                    onChange={e => {
                      field.onChange(e.target.checked);
                    }}
                  />
                }
                label="Customer will pick up order"
              />
            )}
          />

          {!isPickup && (
            <Controller
              name="useBillingAddress"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={field.value || false}
                      onChange={e => {
                        const checked = e.target.checked;
                        field.onChange(checked);

                        // Clear shipping address fields when using billing address
                        if (checked) {
                          clearShippingAddressFields();
                        }

                        // Explicitly mark form as dirty to enable Update button
                        setValue('stateConfirmed', false, { shouldDirty: true });
                      }}
                    />
                  }
                  label="Use billing address as shipping address"
                />
              )}
            />
          )}
        </Box>
      </Grid>

      {!isPickup && (
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {useBillingAddress ? (
              <>
                <Grid item xs={12}>
                  <ReadOnlyField label="Address Line 1" value={billingAddress?.address1 || ''} />
                </Grid>
                <Grid item xs={12}>
                  <ReadOnlyField label="Address Line 2" value={billingAddress?.address2 || ''} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <ReadOnlyField label="City" value={billingAddress?.city || ''} />
                </Grid>
                <Grid item xs={6} sm={3}>
                  <ReadOnlyField label="State" value={billingAddress?.state || ''} />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <ReadOnlyField label="Zip Code" value={billingAddress?.zip || ''} />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <ReadOnlyField
                    label="Phone Number"
                    value={billingAddress?.phone ? formatPhoneNumber(billingAddress.phone) : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <ReadOnlyField
                    label="Fax Number"
                    value={billingAddress?.fax ? formatPhoneNumber(billingAddress.fax) : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={7}>
                  <ReadOnlyField label="Email Address" value={billingAddress?.email || ''} />
                </Grid>
              </>
            ) : (
              <>
                <AddressFields
                  control={control}
                  address1Name="shippingAddress.address1"
                  address2Name="shippingAddress.address2"
                  cityName="shippingAddress.city"
                  stateName="shippingAddress.state"
                  zipName="shippingAddress.zip"
                  isReadOnly={false}
                  allFieldsRequired={true}
                  fieldRequirements={{
                    address1Required: true,
                    address2Required: false,
                    cityRequired: true,
                    stateRequired: true,
                    zipRequired: true,
                  }}
                />
                <Grid item xs={12} sm={5}>
                  <PhoneNumberField
                    name="shippingAddress.phone"
                    control={control}
                    formatPhoneNumber={formatPhoneNumber}
                    required={false}
                    label="Phone Number"
                    isReadOnly={false}
                  />
                </Grid>
              </>
            )}

            {/* Require confirmation only when the edited shipping state differs from building state */}
            {showConfirm && (
              <Grid item xs={12}>
                <Controller
                  name="stateConfirmed"
                  control={control}
                  render={({ field, fieldState }) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                      <Checkbox
                        checked={!!field.value}
                        onChange={async e => {
                          setValue('stateConfirmed', e.target.checked, {
                            shouldValidate: true,
                            shouldDirty: true,
                            shouldTouch: true,
                          });
                          await trigger(); // ensure parent dialog sees valid/dirty state update
                        }}
                        sx={{ mr: 1 }}
                      />
                      <Box>
                        <Typography>
                          Confirm the building is being erected in a different state (
                          {buildingState}) than the shipping state ({editedShippingState}). Please
                          note that changing the state may affect your building specifications,
                          including classifications and minimum load ratings.
                        </Typography>
                        {fieldState.error && (
                          <Typography color="error" variant="caption">
                            {fieldState.error.message}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  )}
                />
              </Grid>
            )}
          </Grid>
        </Grid>
      )}
    </Grid>
  );
};

const OrderShippingDialog: React.FC<OrderShippingDialogProps> = ({
  open,
  onClose,
  order,
  onSubmit,
  isSubmitting = false,
}) => {
  const handleSubmit = async (data: OrderFormData) => {
    const cleaned = { ...(data as Record<string, unknown>) };
    delete (cleaned as Record<string, unknown>).stateConfirmed;
    await onSubmit(cleaned as unknown as OrderShippingFormData);
  };

  return (
    <OrderUpdateDialog
      open={open}
      onClose={onClose}
      order={order}
      title="Edit Shipping Information"
      schemaKey="shipping"
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
    >
      <OrderShippingForm />
    </OrderUpdateDialog>
  );
};

export default OrderShippingDialog;
